import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/format";
import { toast } from "sonner";
import { Wallet, ArrowRight, Plus, X, CreditCard, Tag, Target, Check, Loader2 } from "lucide-react";

const ICON_OPTIONS = ["Tag", "UtensilsCrossed", "Car", "Music", "Plug", "ShoppingBag", "Heart", "Briefcase", "Sparkles"];
const COLORS = ["#8B5CF6", "#38BDF8", "#10B981", "#F59E0B", "#F43F5E", "#EC4899", "#3B82F6", "#A78BFA"];
const DEFAULT_CATS = [
  { name: "Salario", type: "income", color: "#10B981", icon: "Briefcase" },
  { name: "Comida", type: "expense", color: "#F59E0B", icon: "UtensilsCrossed" },
  { name: "Transporte", type: "expense", color: "#8B5CF6", icon: "Car" },
  { name: "Entretenimiento", type: "expense", color: "#F43F5E", icon: "Music" },
  { name: "Servicios", type: "expense", color: "#3B82F6", icon: "Plug" },
  { name: "Compras", type: "expense", color: "#EC4899", icon: "ShoppingBag" },
];

export default function OnboardingWizard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cats, setCats] = useState(DEFAULT_CATS);
  const [monthlyBudget, setMonthlyBudget] = useState("");
  const [accounts, setAccounts] = useState([{ name: "Cuenta Principal", type: "checking", currency: "DOP", balance: 0, color: "#8B5CF6" }]);
  const [cards, setCards] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState("DOP");

  useEffect(() => {
    api.get("/onboarding/status").then(r => {
      if (r.data.onboarded) nav("/", { replace: true });
    }).catch(() => {});
  }, [nav]);

  const [skipping, setSkipping] = useState(false);
  // Guards against a double-submit firing the creation call twice (rapid double
  // click, re-render) before `loading` state has flushed to disable the button.
  const submittingRef = useRef(false);

  const next = () => setStep(s => Math.min(4, s + 1));
  const back = () => setStep(s => Math.max(0, s - 1));

  const finish = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    try {
      await api.post("/onboarding/complete", {
        categories: cats,
        monthly_budget: monthlyBudget ? Number(monthlyBudget) : null,
        accounts,
        cards,
        base_currency: baseCurrency,
      });
      toast.success("¡Bienvenido a TradingDock!");
      await refresh();
      nav("/", { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
      submittingRef.current = false;  // allow retry after a failed submit
    } finally { setLoading(false); }
  };

  const skip = async () => {
    if (skipping || loading) return;
    setSkipping(true);
    try {
      // Dismiss the wizard without creating anything. Registration already
      // seeded a default account and categories auto-seed on first use, so the
      // dashboard is fully usable. Marks onboarding dismissed server-side so it
      // doesn't re-appear on next login.
      await api.post("/onboarding/skip");
      await refresh();
      nav("/", { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
      setSkipping(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0a10] text-white px-4 py-8" data-testid="onboarding">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2.5 mb-8">
          <img src="/logo.png" alt="The Trading Dock" className="w-10 h-10 rounded-xl ring-1 ring-white/10 shadow-[0_4px_20px_rgba(139,92,246,0.35)]"/>
          <div className="text-[14px] font-bold tracking-tight">THE TRADING DOCK<span className="text-[10px] align-super text-violet-300">®</span></div>
          <button onClick={skip} disabled={skipping || loading} data-testid="onb-skip"
            className="ml-auto text-sm text-slate-400 hover:text-white disabled:opacity-50 transition-colors">
            {skipping ? "Saltando…" : "Saltar por ahora"}
          </button>
        </div>

        <Progress step={step} total={5}/>

        {step === 0 && <StepWelcome name={user?.name} onNext={next}/>}
        {step === 1 && <StepCategories cats={cats} setCats={setCats} onNext={next} onBack={back}/>}
        {step === 2 && <StepBudget value={monthlyBudget} setValue={setMonthlyBudget} currency={baseCurrency} setCurrency={setBaseCurrency} onNext={next} onBack={back}/>}
        {step === 3 && <StepAccounts accounts={accounts} setAccounts={setAccounts} baseCurrency={baseCurrency} onNext={next} onBack={back}/>}
        {step === 4 && <StepCards cards={cards} setCards={setCards} accounts={accounts} onFinish={finish} onBack={back} loading={loading}/>}
      </div>
    </div>
  );
}

function Progress({ step, total }) {
  return (
    <div className="flex items-center gap-2 mb-10">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i <= step ? "bg-gradient-to-r from-violet-500 to-fuchsia-500" : "bg-white/5"}`}/>
      ))}
    </div>
  );
}

function StepWelcome({ name, onNext }) {
  return (
    <div data-testid="step-welcome">
      <div className="text-xs uppercase tracking-[0.2em] text-violet-300/70 font-semibold mb-3">Paso 1 de 5</div>
      <h1 className="text-4xl sm:text-5xl font-bold leading-tight">¡Hola, {name?.split(" ")[0]}! 👋</h1>
      <p className="mt-4 text-slate-400 text-base leading-relaxed">
        Vamos a configurar tu panel de finanzas en menos de 2 minutos. Esto nos ayudará a personalizar tu experiencia.
      </p>
      <div className="grid sm:grid-cols-2 gap-3 mt-8">
        <FeatureCard icon={Tag} title="Categorías" desc="Personaliza tus categorías de ingresos y gastos."/>
        <FeatureCard icon={Target} title="Presupuesto" desc="Define tu límite mensual de gastos."/>
        <FeatureCard icon={Wallet} title="Cuentas" desc="Agrega tus cuentas bancarias."/>
        <FeatureCard icon={CreditCard} title="Tarjetas" desc="Vincula tarjetas de débito y crédito."/>
      </div>
      <NavBtns onNext={onNext} hideBack/>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc }) {
  return (
    <div className="card-surface p-5">
      <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center">
        <Icon className="w-5 h-5 text-violet-300"/>
      </div>
      <div className="mt-3 font-semibold">{title}</div>
      <div className="text-sm text-slate-400 mt-1">{desc}</div>
    </div>
  );
}

function StepCategories({ cats, setCats, onNext, onBack }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("expense");
  const [color, setColor] = useState("#8B5CF6");
  const [icon, setIcon] = useState("Tag");
  const add = () => {
    if (!name) return;
    setCats([...cats, { name, type, color, icon }]);
    setName("");
  };
  const remove = (i) => setCats(cats.filter((_, idx) => idx !== i));
  return (
    <div data-testid="step-categories">
      <div className="text-xs uppercase tracking-[0.2em] text-violet-300/70 font-semibold mb-3">Paso 2 de 5</div>
      <h1 className="text-3xl sm:text-4xl font-bold">Tus categorías</h1>
      <p className="mt-3 text-slate-400">Edita o agrega categorías personalizadas. Puedes cambiarlas después.</p>

      <div className="card-surface p-5 mt-6 space-y-2">
        {cats.map((c, i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/[0.03]" data-testid={`onb-cat-${i}`}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold" style={{ background: `${c.color}22`, color: c.color }}>
              {c.name[0]}
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium">{c.name}</div>
              <div className="text-[11px] text-slate-500 capitalize">{c.type === "income" ? "Ingreso" : "Gasto"}</div>
            </div>
            <button onClick={() => remove(i)} className="text-slate-500 hover:text-rose-400 p-1.5"><X className="w-4 h-4"/></button>
          </div>
        ))}
      </div>

      <div className="card-surface p-5 mt-4">
        <div className="text-sm font-semibold mb-3">Agregar categoría</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Ej: Gimnasio" data-testid="onb-cat-name"
            className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-slate-600"/>
          <select value={type} onChange={e => setType(e.target.value)} data-testid="onb-cat-type"
            className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
            <option value="expense" className="bg-[#15141b]">Gasto</option>
            <option value="income" className="bg-[#15141b]">Ingreso</option>
          </select>
        </div>
        <div className="mt-3 flex gap-2 flex-wrap">
          {COLORS.map(c => (
            <button key={c} onClick={() => setColor(c)} className={`w-7 h-7 rounded-full transition ${color === c ? "ring-2 ring-white" : ""}`} style={{ background: c }}/>
          ))}
        </div>
        <button onClick={add} disabled={!name} data-testid="onb-cat-add"
          className="mt-4 flex items-center gap-2 text-sm bg-white/5 hover:bg-violet-500/15 hover:text-violet-300 px-4 py-2 rounded-xl border border-white/10 disabled:opacity-40">
          <Plus className="w-4 h-4"/> Agregar
        </button>
      </div>

      <NavBtns onNext={onNext} onBack={onBack}/>
    </div>
  );
}

function StepBudget({ value, setValue, currency, setCurrency, onNext, onBack }) {
  return (
    <div data-testid="step-budget">
      <div className="text-xs uppercase tracking-[0.2em] text-violet-300/70 font-semibold mb-3">Paso 3 de 5</div>
      <h1 className="text-3xl sm:text-4xl font-bold">Tu presupuesto mensual</h1>
      <p className="mt-3 text-slate-400">¿Cuánto planeas gastar al mes? Esto nos ayuda a alertarte cuando te acerques al límite.</p>

      <div className="card-surface p-6 mt-6">
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">Divisa base</div>
        <select value={currency} onChange={e => setCurrency(e.target.value)} data-testid="onb-currency"
          className="bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-sm w-full sm:w-48 mb-4">
          <option value="DOP" className="bg-[#15141b]">DOP — Peso Dominicano</option>
          <option value="USD" className="bg-[#15141b]">USD — Dólar</option>
          <option value="EUR" className="bg-[#15141b]">EUR — Euro</option>
          <option value="MXN" className="bg-[#15141b]">MXN — Peso Mexicano</option>
          <option value="COP" className="bg-[#15141b]">COP — Peso Colombiano</option>
        </select>

        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">Presupuesto mensual ({currency})</div>
        <input type="number" min="0" value={value} onChange={e => setValue(e.target.value)} placeholder="50000" data-testid="onb-budget"
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-2xl font-bold"/>
        <div className="text-xs text-slate-500 mt-2">Puedes saltarte este paso si prefieres no establecer un límite.</div>
      </div>

      <NavBtns onNext={onNext} onBack={onBack}/>
    </div>
  );
}

function StepAccounts({ accounts, setAccounts, baseCurrency, onNext, onBack }) {
  const add = () => setAccounts([...accounts, { name: "", type: "savings", currency: baseCurrency, balance: 0, color: "#38BDF8" }]);
  const update = (i, k, v) => setAccounts(accounts.map((a, idx) => idx === i ? { ...a, [k]: v } : a));
  const remove = (i) => setAccounts(accounts.filter((_, idx) => idx !== i));
  return (
    <div data-testid="step-accounts">
      <div className="text-xs uppercase tracking-[0.2em] text-violet-300/70 font-semibold mb-3">Paso 4 de 5</div>
      <h1 className="text-3xl sm:text-4xl font-bold">Tus cuentas</h1>
      <p className="mt-3 text-slate-400">Agrega tus cuentas bancarias, de ahorro o efectivo. Las tarjetas las anclaremos después.</p>

      <div className="space-y-3 mt-6">
        {accounts.map((a, i) => (
          <div key={i} className="card-surface p-5" data-testid={`onb-account-${i}`}>
            <div className="grid sm:grid-cols-4 gap-3">
              <input value={a.name} onChange={e => update(i, "name", e.target.value)} placeholder="Nombre" className="sm:col-span-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
              <select value={a.type} onChange={e => update(i, "type", e.target.value)} className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                <option value="checking" className="bg-[#15141b]">Corriente</option>
                <option value="savings" className="bg-[#15141b]">Ahorros</option>
                <option value="cash" className="bg-[#15141b]">Efectivo</option>
                <option value="investment" className="bg-[#15141b]">Inversión</option>
              </select>
              <select value={a.currency} onChange={e => update(i, "currency", e.target.value)} className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                <option value="DOP" className="bg-[#15141b]">DOP</option>
                <option value="USD" className="bg-[#15141b]">USD</option>
                <option value="EUR" className="bg-[#15141b]">EUR</option>
                <option value="MXN" className="bg-[#15141b]">MXN</option>
                <option value="COP" className="bg-[#15141b]">COP</option>
              </select>
            </div>
            <div className="flex items-center gap-3 mt-3">
              <input type="number" value={a.balance} onChange={e => update(i, "balance", Number(e.target.value))} placeholder="Saldo" className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
              {accounts.length > 1 && (
                <button onClick={() => remove(i)} className="p-2 text-slate-500 hover:text-rose-400"><X className="w-4 h-4"/></button>
              )}
            </div>
          </div>
        ))}
        <button onClick={add} data-testid="onb-account-add"
          className="w-full flex items-center justify-center gap-2 text-sm text-violet-300 bg-white/[0.03] hover:bg-violet-500/10 border border-dashed border-white/10 hover:border-violet-500/30 py-3 rounded-xl">
          <Plus className="w-4 h-4"/> Agregar otra cuenta
        </button>
      </div>

      <NavBtns onNext={onNext} onBack={onBack}/>
    </div>
  );
}

function StepCards({ cards, setCards, accounts, onFinish, onBack, loading }) {
  const add = (kind) => setCards([...cards, {
    kind, name: "", last4: "", brand: "Visa", currency: "DOP",
    account_id: kind === "debit" ? (accounts[0]?.name || "") : null,
    limit: kind === "credit" ? 0 : null, color: "#8B5CF6"
  }]);
  const update = (i, k, v) => setCards(cards.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const remove = (i) => setCards(cards.filter((_, idx) => idx !== i));
  return (
    <div data-testid="step-cards">
      <div className="text-xs uppercase tracking-[0.2em] text-violet-300/70 font-semibold mb-3">Paso 5 de 5</div>
      <h1 className="text-3xl sm:text-4xl font-bold">Tus tarjetas</h1>
      <p className="mt-3 text-slate-400">Vincula tarjetas de débito a una cuenta o agrega tarjetas de crédito. Opcional.</p>

      <div className="space-y-3 mt-6">
        {cards.map((c, i) => (
          <div key={i} className="card-surface p-5" data-testid={`onb-card-${i}`}>
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${c.kind === "credit" ? "bg-rose-500/15 text-rose-300" : "bg-emerald-500/15 text-emerald-300"}`}>
                {c.kind === "credit" ? "Crédito" : "Débito"}
              </span>
              <button onClick={() => remove(i)} className="ml-auto text-slate-500 hover:text-rose-400"><X className="w-4 h-4"/></button>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <input value={c.name} onChange={e => update(i, "name", e.target.value)} placeholder="Nombre (ej: Visa Popular)" className="sm:col-span-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
              <input value={c.last4} onChange={e => update(i, "last4", e.target.value.slice(0,4))} placeholder="Últimos 4" maxLength={4} className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              <select value={c.brand} onChange={e => update(i, "brand", e.target.value)} className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                <option className="bg-[#15141b]">Visa</option><option className="bg-[#15141b]">Mastercard</option>
                <option className="bg-[#15141b]">Amex</option><option className="bg-[#15141b]">Otro</option>
              </select>
              <select value={c.currency} onChange={e => update(i, "currency", e.target.value)} className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                <option className="bg-[#15141b]">DOP</option><option className="bg-[#15141b]">USD</option><option className="bg-[#15141b]">EUR</option>
              </select>
              {c.kind === "credit" ? (
                <input type="number" value={c.limit || 0} onChange={e => update(i, "limit", Number(e.target.value))} placeholder="Límite crédito" className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
              ) : (
                <select value={c.account_id || ""} onChange={e => update(i, "account_id", e.target.value)} className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                  {accounts.map((a, idx) => <option key={idx} value={a.name} className="bg-[#15141b]">{a.name}</option>)}
                </select>
              )}
            </div>
          </div>
        ))}
        <div className="grid sm:grid-cols-2 gap-3">
          <button onClick={() => add("debit")} data-testid="onb-add-debit"
            className="flex items-center justify-center gap-2 text-sm text-emerald-300 bg-white/[0.03] hover:bg-emerald-500/10 border border-dashed border-white/10 hover:border-emerald-500/30 py-3 rounded-xl">
            <Plus className="w-4 h-4"/> Tarjeta de débito
          </button>
          <button onClick={() => add("credit")} data-testid="onb-add-credit"
            className="flex items-center justify-center gap-2 text-sm text-rose-300 bg-white/[0.03] hover:bg-rose-500/10 border border-dashed border-white/10 hover:border-rose-500/30 py-3 rounded-xl">
            <Plus className="w-4 h-4"/> Tarjeta de crédito
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between mt-10">
        <button onClick={onBack} className="text-sm text-slate-400 hover:text-white">← Atrás</button>
        <button onClick={onFinish} disabled={loading} data-testid="onb-finish"
          className="flex items-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold px-6 py-3 rounded-xl shadow-[0_8px_30px_-10px_rgba(139,92,246,0.6)] disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Finalizar <Check className="w-4 h-4"/></>}
        </button>
      </div>
    </div>
  );
}

function NavBtns({ onNext, onBack, hideBack, nextLabel = "Continuar" }) {
  return (
    <div className="flex items-center justify-between mt-10">
      {hideBack ? <span/> : <button onClick={onBack} data-testid="onb-back" className="text-sm text-slate-400 hover:text-white">← Atrás</button>}
      <button onClick={onNext} data-testid="onb-next"
        className="flex items-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold px-6 py-3 rounded-xl shadow-[0_8px_30px_-10px_rgba(139,92,246,0.6)]">
        {nextLabel} <ArrowRight className="w-4 h-4"/>
      </button>
    </div>
  );
}
